/**
 * Utility functions for calculating artwork dimensions and scaling
 */

/**
 * Convert artwork height to inches based on unit
 * @param {number} height - The height value
 * @param {string} unit - The unit ('cm' or 'in')
 * @returns {number|null} Height in inches or null if invalid
 */
export const convertToInches = (height, unit) => {
  if (!height || !unit) return null;
  return unit === 'cm' ? height / 2.54 : height;
};

/**
 * Convert meters to feet
 * @param {number} meters - The value in meters
 * @returns {number} Value in feet
 */
export const metersToFeet = (meters) => {
  if (!meters || typeof meters !== 'number') return null;
  return meters * 3.28084;
};

/**
 * Calculate artwork dimensions from listing data
 * @param {Object} listing - The listing object
 * @returns {Object} Object containing artworkHeightInInches and dimensionUnit
 */
export const getArtworkDimensions = (listing) => {
  const publicData = listing?.attributes?.publicData;
  const artworkHeightValue = publicData?.height;
  const dimensionUnit = publicData?.dimensionUnit;

  const artworkHeightInInches = convertToInches(artworkHeightValue, dimensionUnit);

  return {
    artworkHeightInInches,
    dimensionUnit,
    artworkHeightValue,
  };
};

/**
 * Calculate artwork ratio relative to wall height
 * @param {number} artworkHeightInInches - Artwork height in inches
 * @param {number} wallHeightInFeet - Wall height in feet (default 8ft for grid mockup, 9ft for room mockup)
 * @param {number|null} wallHeightInMeters - Optional: actual wall height in meters from reconstruction API
 * @returns {number} Ratio of artwork height to wall height
 */
export const calculateArtworkRatio = (artworkHeightInInches, wallHeightInFeet = 8, wallHeightInMeters = null) => {
  // If we have actual wall height in meters, use it (most accurate)
  let actualWallHeightInFeet = wallHeightInFeet;
  if (wallHeightInMeters) {
    const convertedHeight = metersToFeet(wallHeightInMeters);
    if (convertedHeight) {
      actualWallHeightInFeet = convertedHeight;
    }
  }

  if (!artworkHeightInInches) {
    // Fallback to 3ft artwork on actual/default wall height if artwork dimensions not available
    return 3 / actualWallHeightInFeet;
  }

  const wallHeightInInches = actualWallHeightInFeet * 12;
  return artworkHeightInInches / wallHeightInInches;
};

/**
 * Calculate distance scale factor for shadow/border based on polygon height
 * Higher ratio = closer to wall = more pronounced shadow/border
 * @param {number} polygonHeightNormalized - Height of wall polygon (0-1 range)
 * @returns {number} Scale factor between 0.3 and 3
 */
export const calculateDistanceScaleFactor = (polygonHeightNormalized) => {
  if (!polygonHeightNormalized) return 1;

  // Scale from 0.3x (far, subtle) to 3x (close, pronounced)
  return Math.max(0.3, Math.min(3, 0.3 + polygonHeightNormalized * 2.7));
};

/**
 * Get shadow style for artwork based on distance scale factor
 * @param {number} scaleFactor - Distance scale factor
 * @returns {string} CSS box-shadow value
 */
export const getArtworkShadow = (scaleFactor = 1) => {
  const offsetX = 0.7 * scaleFactor;
  const offsetY = 0.7 * scaleFactor;
  const blur = 1.4 * scaleFactor;
  return `${offsetX}px ${offsetY}px ${blur}px 0px rgba(0, 0, 0, 0.3)`;
};

/**
 * Get border style for artwork based on distance scale factor
 * @param {number} scaleFactor - Distance scale factor
 * @returns {string} CSS border-bottom value
 */
export const getArtworkBorder = (scaleFactor = 1) => {
  const width = 0.3 * scaleFactor;
  return `${width}px solid rgba(0, 0, 0, 0.45)`;
};

/**
 * Calculate artwork pixel height based on container and ratio
 * @param {number} containerHeight - Container height in pixels
 * @param {number} artworkRatio - Artwork to wall height ratio
 * @param {number} wallHeightRatio - Wall height as ratio of container (default 1/3 for grid mockup)
 * @returns {number} Artwork height in pixels
 */
export const calculateArtworkPixelHeight = (containerHeight, artworkRatio, wallHeightRatio = 1 / 3) => {
  const wallHeightPx = containerHeight * wallHeightRatio;
  return artworkRatio * wallHeightPx;
};
